/**
 * Opciones del picker [ATAQUE] a partir del inventario del PJ.
 * Espejo de server/utils/inventorySlots.js → listAtaqueSourceOptions.
 */

const UNARMED_WHITE = Object.freeze({ min: 1, max: 8 })
const WEAPON_ITEM_TYPES = new Set(['weapon', 'sword', 'axe', 'bow', 'staff'])

function parseItemDamageRange(item) {
  if (!item?.damage) {
    return { min: UNARMED_WHITE.min, max: UNARMED_WHITE.max }
  }
  const parts = String(item.damage)
    .split('-')
    .map((n) => Number(n) || 0)
  const min = Math.max(0, parts[0] || 0)
  const max = Math.max(min, parts[1] != null ? parts[1] : min)
  if (min <= 0 && max <= 0) {
    return { min: UNARMED_WHITE.min, max: UNARMED_WHITE.max }
  }
  return { min: min || UNARMED_WHITE.min, max: max || UNARMED_WHITE.max }
}

function isWeaponItemType(item) {
  if (!item?.type) return false
  return WEAPON_ITEM_TYPES.has(String(item.type).toLowerCase())
}

function itemKey(item) {
  if (!item) return null
  if (item.id != null) return `id:${Number(item.id)}`
  return `name:${String(item.name || '')}`
}

/**
 * @param {{ equipment?: object, inventoryItems?: array }} opts
 */
export function listAtaqueSourceOptions({ equipment, inventoryItems = [] } = {}) {
  const options = [
    {
      source: 'puños',
      label: 'Puños',
      badge: 'Cuerpo',
      section: 'cuerpo',
      ...UNARMED_WHITE,
      damageSuggested: UNARMED_WHITE.min,
    },
    {
      source: 'patada',
      label: 'Patada',
      badge: 'Cuerpo',
      section: 'cuerpo',
      ...UNARMED_WHITE,
      damageSuggested: UNARMED_WHITE.min,
    },
  ]

  if (equipment?.mainHand) {
    const range = parseItemDamageRange(equipment.mainHand)
    options.push({
      source: 'principal',
      label: String(equipment.mainHand.name || 'Arma principal'),
      badge: 'Mano principal',
      section: 'mano',
      min: range.min,
      max: range.max,
      damageSuggested: range.min,
      itemId: equipment.mainHand.id != null ? Number(equipment.mainHand.id) : null,
    })
  }

  if (equipment?.offHand && isWeaponItemType(equipment.offHand)) {
    const range = parseItemDamageRange(equipment.offHand)
    options.push({
      source: 'secundaria',
      label: String(equipment.offHand.name || 'Arma secundaria'),
      badge: 'Secundaria',
      section: 'mano',
      min: range.min,
      max: range.max,
      damageSuggested: range.min,
      itemId: equipment.offHand.id != null ? Number(equipment.offHand.id) : null,
    })
  }

  const mainKey = itemKey(equipment?.mainHand)
  const offKey = itemKey(equipment?.offHand)
  for (const item of inventoryItems || []) {
    if (!item || !isWeaponItemType(item) || !item.isThrowable) continue
    const key = itemKey(item)
    if (key && (key === mainKey || key === offKey)) continue
    const qty = item.quantity != null ? Number(item.quantity) : 1
    if (Number.isFinite(qty) && qty <= 0) continue
    if (item.id == null) continue
    const range = parseItemDamageRange(item)
    options.push({
      source: `objeto:${Number(item.id)}`,
      label: String(item.name || `Objeto #${item.id}`),
      badge: 'Arrojadiza',
      section: 'arrojadiza',
      min: range.min,
      max: range.max,
      damageSuggested: range.min,
      itemId: Number(item.id),
      quantity: qty,
    })
  }

  return options
}
